import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * SoftBack — defensive back-button handler for SPAs on mobile.
 *
 * Problem: when the user opens a deep link directly (e.g. shared on WhatsApp:
 * https://promo-directorio.emergent.host/directorio/06700), the browser's
 * history for that tab has only ONE entry. Tapping the device "back" button
 * exits the page entirely.
 *
 * Fix: if we detect that the user landed directly on a non-root page AND we
 * are the first entry in history for the current tab, we inject a phantom
 * "/" entry BEFORE the current URL. This way the device "back" button takes
 * the user to the home page (still inside the app) instead of leaving.
 *
 * Place once near the top of the <BrowserRouter> subtree (after ScrollToTop).
 */
export default function SoftBack() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Only act on the very first mount, never on subsequent route changes.
    // After the first navigation, the browser already has 2+ history entries
    // and the device back button works as expected.
    if (window.__softBackInjected) return;
    window.__softBackInjected = true;

    // If user landed on the home page, nothing to do
    if (pathname === "/") return;

    // If we already have multiple entries (>1), the user navigated here from
    // somewhere inside the app — no need to intervene.
    // history.length is unreliable across browsers but >2 is a safe signal.
    if (window.history.length > 2) return;

    // Inject a phantom "/" entry BEFORE the current one
    try {
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      window.history.replaceState(window.history.state, "", "/");
      window.history.pushState({ softBack: true }, "", currentUrl);
    } catch (e) {
      console.debug("SoftBack: history injection failed", e?.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
